// (C) 2022 GoodData Corporation
import { LRUCache } from "lru-cache";
import { DefaultFormatterFacade } from "./facade.js";
import { ClientColors, DefaultFormat, FormatOptions, ResultMetadata } from "./gooddata_format.js";
/**
 * Max item in internal cache
 */
const FORMATTER_CACHE_SIZE = 200;
/**
 * `ClientFormatterFacade` serves as an interface for converting and formatting values.
 *
 * This class provides utilities for converting diverse data types into their numeric representations
 * and for formatting numbers according to specified formats and separators.
 *
 * @remarks
 * This facade encapsulates the details and complexities of number and string transformations,
 * ensuring that conversions and formatting are consistent and reliable.
 *
 * @public
 */
class ClientFormatterFacade {
    /**
     * Converts diverse data types (string, number, null, undefined) into a numerical representation.
     *
     * @remarks
     * This function handles a variety of input types and returns the appropriate numeric representation.
     * - Returns `null` if the input is `null`.
     * - Returns `NaN` if the input is `undefined`.
     * - Returns the number as-is if the input is already a number.
     * - Attempts to parse and return the input as a number if it's a string.
     *
     * @param value - The input value to be converted.
     * @returns The converted number value or `null`.
     */
    static convertValue(value) {
        if (value === null) {
            return null;
        }
        if (value === undefined) {
            return NaN;
        }
        if (typeof value === "number") {
            return value;
        }
        return parseFloat(value);
    }
    /**
     * Formats a given value based on the specified format and separators.
     *
     * @remarks
     * This method attempts to format values according to the provided format and separators.
     * If the formatting process encounters any issues, it reverts to a default format.
     * The resulting format may include translated hex colors with a '#' prefix.
     * This method utilizes a caching mechanism to store compiled formatters for improved performance.
     *
     * @param value - The numeric value (or null) to be formatted.
     * @param format - The format string to be applied. Uses a default format if none is provided.
     * @param separators - Separators for thousands and decimals to be used in formatting.
     * @returns An object containing the formatted value and associated details, like colors.
     */
    static formatValue(value, format = DefaultFormat, separators) {
        const facade = new DefaultFormatterFacade();
        const option = new FormatOptions();
        option.decimalSeparator = separators === null || separators === void 0 ? void 0 : separators.decimal;
        option.thousandsSeparator = separators === null || separators === void 0 ? void 0 : separators.thousand;
        option.includeColors = false;
        const metadata = new ResultMetadata();
        try {
            // We use cache for compiled formatter for given format string
            let formatter = ClientFormatterFacade.cache.get(format);
            if (!formatter) {
                formatter = facade.compileFormatter(format);
                ClientFormatterFacade.cache.set(format, formatter);
            }
            const result = formatter.format(value, option, metadata);
            return {
                colors: new ClientColors(metadata.colors),
                formattedValue: result,
                nullConditionFormatter: metadata.nullConditionFormatter,
                effectiveFormat: metadata.effectiveFormat,
                resolvedFormat: formatter.resolve(value, option),
            };
        }
        catch (_a) {
            // in case unexpected error, return result with default format
            metadata.reset();
            // We use cache for compiled formatter for given format string
            let defaultFormatter = ClientFormatterFacade.cache.get(DefaultFormat);
            if (!defaultFormatter) {
                defaultFormatter = facade.compileFormatter(format);
                ClientFormatterFacade.cache.set(DefaultFormat, defaultFormatter);
            }
            const result = defaultFormatter.format(value, option, metadata);
            return {
                colors: new ClientColors(metadata.colors),
                formattedValue: result,
                nullConditionFormatter: metadata.nullConditionFormatter,
                effectiveFormat: metadata.effectiveFormat,
                resolvedFormat: defaultFormatter.resolve(value, option),
            };
        }
    }
}
ClientFormatterFacade.cache = new LRUCache({ max: FORMATTER_CACHE_SIZE });
export { ClientFormatterFacade };
//# sourceMappingURL=clientFacade.js.map